import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../common/Button';
import { useAuth } from '../../hooks/useAuth';
import { useChatbotSession, ChatbotMessage } from '../../contexts/ChatbotSessionContext';
import { chatbotApi } from '../../services/chatbotApi';
import { useEmergencyStatus } from '../../hooks/useEmergencyStatus';
import { toast } from 'react-toastify';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';

interface AIChatInterfaceProps {
  userType?: 'patient' | 'staff' | 'visitor';
  context?: Record<string, any>;
  onEscalation?: (sessionId: string, reason?: string) => void;
  onNewMessage?: () => void;
  className?: string;
  useWebSocket?: boolean;
  widgetMode?: boolean; // New prop for widget mode
  sessionId?: string; // Allow external session ID
  messageInput?: string; // External message input control
  onMessageInputChange?: (value: string) => void; // External message input handler
}

export const AIChatInterface: React.FC<AIChatInterfaceProps> = ({
  userType = 'patient',
  context = {},
  onEscalation,
  onNewMessage,
  className = '',
  widgetMode = false,
  sessionId: externalSessionId,
  messageInput: externalMessageInput,
  onMessageInputChange
}) => {
  const { user, isAuthenticated } = useAuth();
  const { currentSession, createNewSession, addMessage, selectSession } = useChatbotSession();
  const draftSessionKey = externalSessionId || currentSession?.session_id || 'new';
  const isExternallyControlled = externalMessageInput !== undefined;
  const { initialValue: draftValue, saveDraft, clearDraft } = useDraftAutosave({
    draftKey: `ai_${draftSessionKey}`,
  });
  const [internalMessageInput, setInternalMessageInput] = useState(isExternallyControlled ? '' : draftValue);

  // Emergency status monitoring
  const { 
    isEmergencyActive, 
    isAIBlocked, 
    blockingReason, 
    reportBlockedAccess 
  } = useEmergencyStatus({
    showToastNotifications: true,
    onStatusChange: (event) => {
      console.log('[AIChatInterface] Emergency status changed:', event);
      if (event.type === 'status_change' && event.status === 'emergency_shutdown') {
        // Add emergency notification message to chat
        if (currentSession) {
          const emergencyMessage: Omit<ChatbotMessage, 'id'> = {
            content: "🚨 Emergency Notice: AI chatbot services have been temporarily disabled due to emergency maintenance. Please contact staff directly for urgent medical assistance.",
            sender: 'assistant',
            timestamp: new Date(),
            metadata: {
              medical_safety_level: 'critical',
              system_message: true,
              emergency_shutdown: true
            }
          };
          addMessage(currentSession.session_id, emergencyMessage);
        }
      }
    }
  });
  
  // Use external message input if provided, otherwise use internal state
  const messageInput = externalMessageInput !== undefined ? externalMessageInput : internalMessageInput;
  const setMessageInput = onMessageInputChange || setInternalMessageInput;
  const [isLoading, setIsLoading] = useState(false);
  const [isEscalated, setIsEscalated] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize session if needed
  useEffect(() => {
    if (!currentSession && !externalSessionId) {
      createNewSession(userType, context);
    } else if (externalSessionId && (!currentSession || currentSession.session_id !== externalSessionId)) {
      selectSession(externalSessionId);
    }
  }, [externalSessionId, userType]); // eslint-disable-line react-hooks/exhaustive-deps

  const messages = useMemo(() => currentSession?.messages || [], [currentSession?.messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Restore draft when session changes (internal input only)
  useEffect(() => {
    if (!isExternallyControlled && draftValue) {
      setInternalMessageInput(draftValue);
    }
  }, [draftValue, isExternallyControlled]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [messageInput]);

  // Initialize session with welcome message if empty
  useEffect(() => {
    if (!currentSession) return;
    
    // Check if user is authenticated using the proper auth hook
    if (!isAuthenticated) {
      const authMessage: Omit<ChatbotMessage, 'id'> = {
        content: "⚠️ Authentication required. Please log in to access the AI assistant.",
        sender: 'assistant',
        timestamp: new Date(),
        metadata: {
          medical_safety_level: 'elevated'
        }
      };
      addMessage(currentSession.session_id, authMessage);
      
      return;
    }
    
    // Add welcome message only if no messages exist
    if (messages.length === 0) {
      const welcomeMessage: Omit<ChatbotMessage, 'id'> = {
        content: getWelcomeMessage(userType),
        sender: 'assistant',
        timestamp: new Date(),
        metadata: {
          medical_safety_level: 'standard'
        }
      };
      addMessage(currentSession.session_id, welcomeMessage);
    }
  }, [currentSession, userType, messages.length, addMessage, isAuthenticated]);

  const getWelcomeMessage = (userType: string): string => {
    const userName = user?.full_name || user?.first_name || '';
    const department = context.hospital_department || '';
    const roleDisplay = context.user_role || '';
    const specialty = context.role_specialty || '';
    const unit = context.current_unit || context.primary_unit || '';
    
    switch (userType) {
      case 'patient':
        return `Hello${userName ? ` ${userName}` : ''}! I'm your AI healthcare assistant. I can help you with appointment scheduling, general medical information, understanding your treatment plan, and connecting you with your care team. How can I assist you today?`;
      
      case 'staff':
        let staffMessage = `Hello${userName ? ` ${userName}` : ''}! I'm your AI healthcare assistant.`;
        
        if (roleDisplay) {
          staffMessage += ` I see you're a ${roleDisplay}`;
          if (specialty && specialty !== roleDisplay) {
            staffMessage += ` specializing in ${specialty}`;
          }
          if (department) {
            staffMessage += ` in the ${department} department`;
          }
          if (unit) {
            staffMessage += ` currently assigned to ${unit}`;
          }
          staffMessage += '.';
        }
        
        staffMessage += ` I can help you with patient care coordination, clinical information, scheduling, administrative tasks, and department-specific queries. What do you need assistance with?`;
        return staffMessage;
      
      case 'visitor':
        return `Welcome${userName ? ` ${userName}` : ''}! I'm here to help visitors navigate the hospital and find information. I can provide directions, visiting hours, parking information, and general facility guidance. How may I assist you?`;
      
      default:
        return `Hello${userName ? ` ${userName}` : ''}! I'm your AI assistant. How can I help you today?`;
    }
  };

  const sendMessage = useCallback(async (content: string) => {
    console.log('[AIChatInterface] sendMessage called with:', { 
      content: content?.slice(0, 50) + '...', 
      isLoading, 
      currentSession: !!currentSession,
      isAIBlocked,
      isEmergencyActive
    });
    
    // Basic validation
    if (!content.trim() || isLoading || !currentSession) {
      console.log('[AIChatInterface] sendMessage early return', { 
        contentEmpty: !content.trim(), 
        isLoading, 
        noSession: !currentSession 
      });
      return;
    }

    // Real-time emergency status validation
    if (isAIBlocked) {
      console.log('[AIChatInterface] AI access blocked, reason:', blockingReason);
      
      // Report blocked access attempt
      reportBlockedAccess('chatbot_message', blockingReason || undefined);
      
      // Show user-friendly message
      if (isEmergencyActive) {
        toast.error(
          'AI chatbot is temporarily unavailable due to emergency maintenance. Please contact staff directly for urgent assistance.',
          { toastId: 'chatbot-emergency-blocked', autoClose: 8000 }
        );
      } else {
        toast.warning(
          `AI chatbot is currently unavailable: ${blockingReason || 'Service temporarily disabled'}`,
          { toastId: 'chatbot-service-blocked', autoClose: 6000 }
        );
      }
      
      // Add system message to chat explaining the situation
      const blockingMessage: Omit<ChatbotMessage, 'id'> = {
        content: isEmergencyActive
          ? "⚠️ I'm currently unavailable due to emergency maintenance. Please contact hospital staff directly for immediate assistance."
          : `⚠️ I'm temporarily unavailable: ${blockingReason || 'Service is being updated'}. Please try again later.`,
        sender: 'assistant',
        timestamp: new Date(),
        metadata: {
          medical_safety_level: isEmergencyActive ? 'critical' : 'elevated',
          system_message: true,
          blocked_access: true
        }
      };
      addMessage(currentSession.session_id, blockingMessage);
      
      return;
    }

    const userMessage: Omit<ChatbotMessage, 'id'> = {
      content: content.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    console.log('AIChatInterface: Adding user message:', userMessage);
    addMessage(currentSession.session_id, userMessage);
    setMessageInput('');
    if (!isExternallyControlled) clearDraft();
    setIsLoading(true);

    try {
      console.log('[AI_CHAT_DEBUG] Sending API request...');
      console.log('[AI_CHAT_DEBUG] Current session:', {
        id: currentSession.id,
        session_id: currentSession.session_id,
        title: currentSession.title,
        session_id_type: typeof currentSession.session_id,
        session_id_length: currentSession.session_id?.length
      });
      
      const requestData = {
        message: content.trim(),
        session_id: currentSession.session_id,
        user_type: userType,
        context: {
          ...context,
          user_id: user?.id,
          user_name: user?.full_name,
          is_authenticated: !!user,
          timestamp: new Date().toISOString(),
          widget_mode: widgetMode
        }
      };
      
      console.log('[AI_CHAT_DEBUG] Request data:', requestData);
      
      const data = await chatbotApi.sendMessage(requestData);

      console.log('AIChatInterface: Received API response:', data);

      const aiMessage: Omit<ChatbotMessage, 'id'> = {
        content: data.response,
        sender: 'assistant',
        timestamp: new Date(),
        metadata: {
          guidelines_applied: data.guidelines_applied,
          medical_safety_level: data.medical_safety_level,
          requires_escalation: data.requires_escalation,
          requires_human_review: data.requires_human_review,
          tools_executed: data.tools_executed,
          processing_time_ms: data.processing_time_ms
        }
      };

      console.log('AIChatInterface: Adding AI message:', aiMessage);
      addMessage(currentSession.session_id, aiMessage);
      
      // Trigger callback for new message (for widget notifications)
      if (onNewMessage) {
        onNewMessage();
      }

      // Handle automatic escalation
      if (data.requires_escalation && !isEscalated) {
        setIsEscalated(true);
        if (onEscalation) {
          onEscalation(currentSession.session_id, 'Automatic escalation due to safety guidelines');
        }
      }

    } catch (error: any) {
      console.error('Error sending message:', error);
      
      let errorContent = "I apologize, but I'm experiencing technical difficulties. Please try again or contact our staff for immediate assistance.";
      
      // Handle authentication errors specifically
      if (error.message?.includes('Authentication required')) {
        errorContent = "⚠️ Authentication required. Please log in to use the AI assistant.";
      }
      
      const errorMessage: Omit<ChatbotMessage, 'id'> = {
        content: errorContent,
        sender: 'assistant',
        timestamp: new Date(),
        metadata: {
          medical_safety_level: 'standard'
        }
      };

      if (currentSession) {
        addMessage(currentSession.session_id, errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    currentSession,
    isAIBlocked,
    isEmergencyActive,
    blockingReason,
    reportBlockedAccess,
    addMessage,
    userType,
    context,
    user
  ]);

  const handleSendMessage = () => {
    sendMessage(messageInput);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEscalateToHuman = async () => {
    if (!currentSession) return;
    
    try {
      await chatbotApi.escalateConversation(currentSession.session_id, 'User requested human assistance');
      
      setIsEscalated(true);
      const escalationMessage: Omit<ChatbotMessage, 'id'> = {
        content: "I've connected you with our human staff. They will be with you shortly to provide personalized assistance.",
        sender: 'assistant',
        timestamp: new Date(),
        metadata: {
          medical_safety_level: 'elevated'
        }
      };
      addMessage(currentSession.session_id, escalationMessage);

      if (onEscalation) {
        onEscalation(currentSession.session_id, 'User requested human assistance');
      }
    } catch (error) {
      console.error('Error escalating conversation:', error);
    }
  };

  const getSafetyLevelColor = (level?: string) => {
    switch (level) {
      case 'critical': return 'text-red-600';
      case 'elevated': return 'text-orange-600';
      default: return 'text-green-600';
    }
  };

  const getSafetyLevelIcon = (level?: string) => {
    switch (level) {
      case 'critical': return '🚨';
      case 'elevated': return '⚠️';
      default: return '✅';
    }
  };

  const getPlaceholderText = (userType: string, context: Record<string, any>): string => {
    const specialty = context.role_specialty || '';
    const department = context.hospital_department || '';
    
    switch (userType) {
      case 'staff':
        if (specialty && department) {
          return `Ask me about patient care, ${specialty.toLowerCase()} procedures, ${department.toLowerCase()} protocols...`;
        } else if (specialty) {
          return `Ask me about patient care, ${specialty.toLowerCase()} procedures, clinical guidelines...`;
        } else if (department) {
          return `Ask me about patient care, ${department.toLowerCase()} operations, administrative tasks...`;
        }
        return "Ask me about patient care, clinical procedures, administrative tasks...";
      
      case 'visitor':
        return "Ask me about directions, visiting hours, parking, hospital services...";
      
      case 'patient':
      default:
        return "Ask me about your healthcare, appointments, treatment information...";
    }
  };

  return (
    <div className={`h-full flex flex-col bg-white ${className}`}>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-2 md:p-3 space-y-2 md:space-y-3 bg-gray-50">
        {(() => {
          console.log('AIChatInterface: Rendering messages:', messages);
          return messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] md:max-w-xs lg:max-w-md xl:max-w-lg ${
              message.sender === 'user' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-gray-900 border border-gray-200'
            } rounded-lg px-2 md:px-3 py-2 shadow-sm`}>
              
              {/* Message Content */}
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              
              {/* Authentication Navigation Buttons */}
              {message.sender === 'assistant' && message.content.includes('Authentication required') && (
                <div className="mt-3 space-y-2">
                  <Button
                    onClick={() => window.location.href = '/login'}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Go to Login Page
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/'}
                    variant="secondary"
                    className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Go to Home Page
                  </Button>
                </div>
              )}
              
              {/* Metadata for AI messages */}
              {message.sender === 'assistant' && message.metadata && (
                <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                  
                  {/* Safety Level Indicator */}
                  {message.metadata.medical_safety_level && (
                    <div className="flex items-center space-x-1 text-xs">
                      <span>{getSafetyLevelIcon(message.metadata.medical_safety_level)}</span>
                      <span className={getSafetyLevelColor(message.metadata.medical_safety_level)}>
                        Safety: {message.metadata.medical_safety_level}
                      </span>
                    </div>
                  )}
                  
                  {/* Guidelines Applied */}
                  {message.metadata.guidelines_applied && message.metadata.guidelines_applied > 0 && (
                    <div className="text-xs text-gray-800">
                      📋 {message.metadata.guidelines_applied} guideline{message.metadata.guidelines_applied !== 1 ? 's' : ''} applied
                    </div>
                  )}
                  
                  {/* Tools Used */}
                  {message.metadata.tools_executed && message.metadata.tools_executed.length > 0 && (
                    <div className="text-xs text-gray-800">
                      🛠️ Tools: {message.metadata.tools_executed.join(', ')}
                    </div>
                  )}
                  
                  {/* Escalation Notice */}
                  {message.metadata.requires_escalation && (
                    <div className="text-xs text-orange-600 font-medium">
                      ⚠️ This conversation requires human review
                    </div>
                  )}
                  
                  {/* Processing Time */}
                  {message.metadata.processing_time_ms && (
                    <div className="text-xs text-gray-700">
                      ⏱️ {message.metadata.processing_time_ms}ms
                    </div>
                  )}
                </div>
              )}
              
              {/* Timestamp */}
              <div className={`text-xs mt-2 ${
                message.sender === 'user' ? 'text-white opacity-90' : 'text-gray-700'
              }`}>
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ));
        })()}
        
        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white text-gray-900 px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <span className="text-sm text-gray-900">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 p-2 md:p-3 flex-shrink-0">
        {/* Emergency Status Banner */}
        {isAIBlocked && (
          <div className={`mb-2 md:mb-3 p-2 md:p-3 rounded-lg border ${
            isEmergencyActive
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-yellow-50 border-yellow-200 text-yellow-800'
          }`}>
            <div className="flex items-center">
              <span className="mr-2 text-sm">
                {isEmergencyActive ? '🚨' : '⚠️'}
              </span>
              <div>
                <div className="text-sm md:text-base font-medium">
                  {isEmergencyActive ? 'Emergency Maintenance' : 'Service Unavailable'}
                </div>
                <div className="text-xs md:text-sm">
                  {blockingReason || 'AI chatbot is temporarily disabled'}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-end space-x-1 md:space-x-2">
          {/* Message Input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={messageInput}
              onChange={(e) => { setMessageInput(e.target.value); if (!isExternallyControlled) saveDraft(e.target.value); }}
              onKeyPress={handleKeyPress}
              placeholder={
                isAIBlocked 
                  ? "AI chatbot is currently unavailable..." 
                  : isEscalated 
                    ? "This conversation has been escalated to human staff..." 
                    : getPlaceholderText(userType, context)
              }
              disabled={isEscalated || isAIBlocked}
              className="w-full border border-gray-300 rounded-lg px-2 md:px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[40px] max-h-32 disabled:bg-gray-100 disabled:text-gray-800"
              rows={1}
            />
          </div>
          
          {/* Send Button */}
          <Button
            onClick={handleSendMessage}
            disabled={!messageInput.trim() || isLoading || isEscalated || isAIBlocked}
            loading={isLoading}
            className="px-3 md:px-4 py-2 text-sm md:text-base"
            title={
              isAIBlocked 
                ? `AI chatbot unavailable: ${blockingReason || 'Service disabled'}` 
                : undefined
            }
          >
            <span className="hidden sm:inline">
              {isLoading ? 'Sending...' : isAIBlocked ? 'Unavailable' : 'Send'}
            </span>
            <span className="sm:hidden">
              {isLoading ? '...' : isAIBlocked ? '✗' : '↑'}
            </span>
          </Button>
        </div>
        
        {/* Character Count */}
        {messageInput.length > 500 && (
          <div className="text-xs text-gray-800 mt-1 text-right">
            {messageInput.length}/1000
          </div>
        )}
      </div>
    </div>
  );
};